import { useId, useMemo } from 'react';
import classNames from 'classnames';
import { RadioCardContext } from './RadioCard.context';
import type { tRadioCardGroupProps } from './RadioCard.types';
import './RadioCard.styles.scss';

/**
 * Controlled group of RadioCards. Owns the shared `name`, the selection and
 * the disabled/invalid state, so each card only declares its own `value` and
 * content.
 *
 * @example
 * const [plan, setPlan] = useState('unica');
 *
 * <RadioCardGroup name="recarga" value={plan} onChange={setPlan} orientation="horizontal" equalWidth>
 *   <RadioCard value="unica">
 *     <span className="text-sm font-bold">Recarga única</span>
 *     <span className="text-xs text-base-content/60">Se cobra una sola vez</span>
 *   </RadioCard>
 *   <RadioCard value="automatica">
 *     <span className="text-sm font-bold">Recarga automática frecuente</span>
 *     <span className="text-xs text-base-content/60">Se cobra cada ciclo, no vence</span>
 *   </RadioCard>
 * </RadioCardGroup>
 */
export default function RadioCardGroup(props: tRadioCardGroupProps) {
  const {
    name,
    value,
    onChange,
    orientation = 'vertical',
    equalWidth = false,
    stackBelowMd = false,
    disabled = false,
    error,
    ariaLabel,
    ariaLabelledBy,
    className,
    children,
  } = props;

  const generatedId = useId();
  const errorId = `${generatedId}-error`;
  const hasError = Boolean(error);

  // Memoized so selecting a card does not re-render every sibling through a
  // fresh context object on each parent render.
  const contextValue = useMemo(
    () => ({
      name,
      value,
      onChange,
      disabled,
      invalid: hasError,
      // Equal widths only mean anything once the cards sit in a row.
      equalWidth: equalWidth && orientation === 'horizontal',
    }),
    [name, value, onChange, disabled, hasError, equalWidth, orientation],
  );

  return (
    <div className={className}>
      <fieldset
        role="radiogroup"
        className={classNames('RadioCardGroup', {
          'RadioCardGroup--horizontal': orientation === 'horizontal',
          'RadioCardGroup--stackBelowMd':
            stackBelowMd && orientation === 'horizontal',
        })}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-invalid={hasError || undefined}
        aria-describedby={hasError ? errorId : undefined}
        disabled={disabled}
      >
        <RadioCardContext.Provider value={contextValue}>{children}</RadioCardContext.Provider>
      </fieldset>

      {hasError && (
        <p id={errorId} className="RadioCardGroup__Error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
